import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTheme } from '@mui/material/styles';
import { AI_BRAND_LOGOS } from './AiBrandLogos';
import {
  CONNECT_AI_EVENT,
  CONNECT_AI_PLATFORMS,
  CONNECT_AI_REASONS,
  GIO_MCP_URL,
  captureConnectAiFromSearch,
  consumeConnectAiFromUtm,
  forgetConnectAiReason,
  peekConnectAiReason,
  stripConnectAiSearch,
} from '../utils/connectAiPopup';

/**
 * Popup global da área logada. Montado uma vez no layout protegido.
 * Fecha sozinho: sem query allowlisted e sem pedido explícito, fica fechado.
 */
function StepPhotos({ images, imageFile, fill }) {
  const sources = images || [];
  if (!sources.length) {
    return (
      <Box
        data-testid="connect-ai-photo"
        data-image-file={imageFile || ''}
        sx={{ width: '100%', aspectRatio: '16 / 10', borderRadius: '12px', bgcolor: fill }}
      />
    );
  }
  const single = sources.length === 1;
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: single ? '1fr' : { xs: '1fr', sm: '1fr 1fr' },
        gap: 1,
      }}
    >
      {sources.map((src) => (
        <Box
          key={src}
          component="img"
          src={src}
          alt=""
          data-testid="connect-ai-photo"
          sx={{
            width: '100%',
            height: single ? 400 : 200,
            objectFit: 'contain',
            borderRadius: '12px',
            bgcolor: '#0b0d12',
            display: 'block',
          }}
        />
      ))}
    </Box>
  );
}

export default function ConnectAiPopup() {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [platformId, setPlatformId] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);

  const show = useCallback(() => {
    setOpen(true);
  }, []);

  useEffect(() => {
    const fromUrl = captureConnectAiFromSearch(location.search);
    const pending = fromUrl || peekConnectAiReason();
    if (!pending) return;
    forgetConnectAiReason();
    consumeConnectAiFromUtm();
    show();
    if (!fromUrl) return;
    navigate(
      {
        pathname: location.pathname,
        search: stripConnectAiSearch(location.search),
        hash: location.hash,
      },
      { replace: true }
    );
  }, [location.hash, location.pathname, location.search, navigate, show]);

  useEffect(() => {
    const onRequest = (event) => {
      const reason = String(event?.detail?.reason || '').trim().toLowerCase();
      if (!CONNECT_AI_REASONS.has(reason)) return;
      forgetConnectAiReason();
      show();
    };
    window.addEventListener(CONNECT_AI_EVENT, onRequest);
    return () => window.removeEventListener(CONNECT_AI_EVENT, onRequest);
  }, [show]);

  const handleClose = () => {
    forgetConnectAiReason();
    setOpen(false);
    setCopied(false);
    setPlatformId(null);
    setStepIndex(0);
  };

  const openTutorial = (id) => {
    setPlatformId(id);
    setStepIndex(0);
    setCopied(false);
  };

  const goBack = () => {
    if (stepIndex === 0) {
      setPlatformId(null);
      setCopied(false);
      return;
    }
    setStepIndex((current) => current - 1);
    setCopied(false);
  };

  const copyMcpUrl = async () => {
    try {
      await navigator.clipboard.writeText(GIO_MCP_URL);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const connectByUrl = async (platform) => {
    await copyMcpUrl();
    window.open(platform.href, '_blank', 'noopener,noreferrer');
  };


  const primary = isDark ? '#8b8aed' : '#5b59d6';
  const primaryStrong = '#5b59d6';
  const softFill = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(91,89,214,0.04)';
  const softBorder = isDark ? 'rgba(139,138,237,0.18)' : 'rgba(91,89,214,0.12)';
  const platform = CONNECT_AI_PLATFORMS.find((item) => item.id === platformId) || null;
  const step = platform ? platform.steps[stepIndex] : null;
  const Logo = platform ? AI_BRAND_LOGOS[platform.id] : null;
  const reducedMotion = '@media (prefers-reduced-motion: reduce)';

  const primaryButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: '10px',
    px: 2,
    boxShadow: 'none',
    bgcolor: primaryStrong,
    color: '#fff',
    '&:hover': { bgcolor: '#4a48c4', boxShadow: '0 4px 14px rgba(91,89,214,0.3)' },
  };
  const ghostButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: '10px',
    px: 1.75,
    color: 'text.secondary',
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="connect-ai-title"
      data-testid="connect-ai-popup"
      PaperProps={{
        sx: {
          borderRadius: '20px',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          border: `1px solid ${softBorder}`,
          boxShadow: isDark ? '0 24px 64px rgba(0,0,0,0.6)' : '0 24px 64px rgba(30,27,75,0.18)',
        },
      }}
    >
      <IconButton
        aria-label={t('close')}
        onClick={handleClose}
        size="small"
        sx={{
          position: 'absolute',
          right: 12,
          top: 12,
          zIndex: 2,
          color: 'text.secondary',
          bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.7)',
          '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.12)' : '#fff' },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      {platform && step ? (
        <>
          <DialogTitle id="connect-ai-title" sx={{ pr: 7, pt: 2.5, pb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '12px',
                  bgcolor: '#fff',
                  color: '#0d0d0d',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `1px solid ${softBorder}`,
                  flexShrink: 0,
                }}
              >
                <Logo size={24} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary', lineHeight: 1.2 }}>
                  {t('connectAi.title')}
                </Typography>
                <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.3 }}>
                  {t(platform.labelKey)}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 2 }}>
              {platform.steps.map((_, index) => (
                <Box
                  key={index}
                  aria-hidden="true"
                  sx={{
                    flex: 1,
                    height: 4,
                    borderRadius: 2,
                    bgcolor: index <= stepIndex ? primary : softBorder,
                    transition: 'background-color 200ms ease',
                    [reducedMotion]: { transition: 'none' },
                  }}
                />
              ))}
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.75, fontWeight: 600, whiteSpace: 'nowrap' }}>
                {t('connectAi.progress', { current: stepIndex + 1, total: platform.steps.length })}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pb: 2.5 }}>
            <Box data-testid={`connect-ai-steps-${platform.id}`}>
              <Box sx={{ borderRadius: '14px', overflow: 'hidden', border: `1px solid ${softBorder}` }}>
                <StepPhotos images={step.images} imageFile={step.imageFile} fill={softFill} />
              </Box>
              <Box sx={{ display: 'flex', gap: 1.25, mt: 2, alignItems: 'flex-start' }}>
                <Box
                  aria-hidden="true"
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    bgcolor: primaryStrong,
                    color: '#fff',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: 0.1,
                  }}
                >
                  {stepIndex + 1}
                </Box>
                <Typography variant="body1" sx={{ lineHeight: 1.55 }}>
                  {t(step.textKey)}
                </Typography>
              </Box>
              {step.showUrl ? (
                <Box
                  sx={{
                    mt: 2,
                    px: 1.75,
                    py: 1.25,
                    borderRadius: '12px',
                    bgcolor: softFill,
                    border: `1px dashed ${isDark ? 'rgba(139,138,237,0.4)' : 'rgba(91,89,214,0.3)'}`,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {t('connectAi.linkLabel')}
                  </Typography>
                  <Typography
                    variant="body1"
                    data-testid="connect-ai-url"
                    sx={{
                      wordBreak: 'break-all',
                      fontWeight: 650,
                      fontSize: '1.1rem',
                      lineHeight: 1.35,
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      color: isDark ? '#c4c3ff' : '#5b59d6',
                    }}
                  >
                    {GIO_MCP_URL}
                  </Typography>
                </Box>
              ) : null}
              {copied ? (
                <Box
                  data-testid="connect-ai-copied"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, color: isDark ? '#86efac' : '#15803d' }}
                >
                  <CheckIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {t('connectAi.linkCopied')}
                  </Typography>
                </Box>
              ) : null}
              <Box sx={{ mt: 2.5, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                  onClick={goBack}
                  data-testid="connect-ai-back"
                  startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
                  sx={ghostButtonSx}
                >
                  {t('connectAi.back')}
                </Button>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {step.action === 'open' ? (
                    <Button
                      component="a"
                      href={platform.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="connect-ai-open"
                      endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                      sx={primaryButtonSx}
                    >
                      {t('connectAi.openClaude')}
                    </Button>
                  ) : null}
                  {step.action === 'copy-open' ? (
                    <Button
                      onClick={() => connectByUrl(platform)}
                      data-testid="connect-ai-open"
                      startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                      sx={primaryButtonSx}
                    >
                      {t('connectAi.openAndCopy')}
                    </Button>
                  ) : null}
                  {stepIndex < platform.steps.length - 1 ? (
                    <Button
                      onClick={() => setStepIndex((current) => current + 1)}
                      data-testid="connect-ai-next"
                      endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
                      sx={step.action ? { ...ghostButtonSx, color: primary } : primaryButtonSx}
                    >
                      {t('connectAi.next')}
                    </Button>
                  ) : null}
                </Box>
              </Box>
            </Box>
          </DialogContent>
        </>
      ) : (
        <>
          <Box
            sx={{
              position: 'relative',
              px: 3,
              pt: 4,
              pb: 2.5,
              textAlign: 'center',
              background: isDark
                ? 'radial-gradient(120% 90% at 50% 0%, rgba(91,89,214,0.32) 0%, rgba(91,89,214,0.08) 55%, transparent 100%)'
                : 'radial-gradient(120% 90% at 50% 0%, rgba(91,89,214,0.16) 0%, rgba(167,139,250,0.06) 55%, transparent 100%)',
            }}
          >
            <Box aria-hidden="true" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25, mb: 2 }}>
              <Box
                component="img"
                src="/profile-gio.jpg"
                alt=""
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '16px',
                  objectFit: 'cover',
                  boxShadow: '0 6px 18px rgba(91,89,214,0.35)',
                  border: '2px solid #fff',
                }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {[0, 1, 2].map((dot) => (
                  <Box
                    key={dot}
                    sx={{
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      bgcolor: primary,
                      opacity: 0.35,
                      animation: `connectAiPulse 1.4s ${dot * 0.2}s ease-in-out infinite`,
                      '@keyframes connectAiPulse': {
                        '0%, 100%': { opacity: 0.25 },
                        '50%': { opacity: 1 },
                      },
                      [reducedMotion]: { animation: 'none', opacity: 0.6 },
                    }}
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {CONNECT_AI_PLATFORMS.map((item, index) => {
                  const ItemLogo = AI_BRAND_LOGOS[item.id];
                  return (
                    <Box
                      key={item.id}
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '14px',
                        bgcolor: '#fff',
                        color: '#0d0d0d',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        ml: index ? -1 : 0,
                        border: `2px solid ${isDark ? '#1e1f33' : '#fff'}`,
                        boxShadow: '0 6px 16px rgba(30,27,75,0.15)',
                        transform: `rotate(${index ? 6 : -6}deg)`,
                      }}
                    >
                      <ItemLogo size={24} />
                    </Box>
                  );
                })}
              </Box>
            </Box>
            <DialogTitle
              id="connect-ai-title"
              component="h2"
              sx={{ p: 0, fontWeight: 750, fontSize: '1.4rem', letterSpacing: '-0.02em', lineHeight: 1.25 }}
            >
              {t('connectAi.title')}
            </DialogTitle>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 380, mx: 'auto', lineHeight: 1.5 }}>
              {t('connectAi.body')}
            </Typography>
          </Box>

          <DialogContent sx={{ pt: 0.5, pb: 3 }}>
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mb: 2.5, display: 'grid', gap: 1 }}>
              {['benefit1', 'benefit2', 'benefit3'].map((key) => (
                <Box component="li" key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: isDark ? 'rgba(139,138,237,0.16)' : 'rgba(91,89,214,0.1)',
                      color: primary,
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 14 }} />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {t(`connectAi.${key}`)}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Typography
              sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary', mb: 1 }}
            >
              {t('connectAi.choose')}
            </Typography>
            <Box data-testid="connect-ai-platforms" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
              {CONNECT_AI_PLATFORMS.map((item) => {
                const ItemLogo = AI_BRAND_LOGOS[item.id];
                return (
                  <ButtonBase
                    key={item.id}
                    data-testid={`connect-ai-${item.id}`}
                    onClick={() => openTutorial(item.id)}
                    sx={{
                      justifyContent: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: '14px',
                      textAlign: 'left',
                      border: `1px solid ${softBorder}`,
                      bgcolor: softFill,
                      color: 'text.primary',
                      transition: 'border-color 160ms ease, box-shadow 160ms ease, transform 100ms ease-out',
                      '&:hover': {
                        borderColor: primary,
                        boxShadow: isDark ? '0 6px 20px rgba(0,0,0,0.35)' : '0 6px 20px rgba(91,89,214,0.14)',
                      },
                      '&:hover .connect-ai-arrow': { transform: 'translateX(3px)', color: primary },
                      '&:active': { transform: 'scale(0.98)' },
                      '&:focus-visible': { outline: `2px solid ${primary}`, outlineOffset: 2 },
                      [reducedMotion]: {
                        transition: 'none',
                        '&:active': { transform: 'none' },
                        '&:hover .connect-ai-arrow': { transform: 'none' },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: '12px',
                        bgcolor: '#fff',
                        color: '#0d0d0d',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `1px solid ${softBorder}`,
                        flexShrink: 0,
                      }}
                    >
                      <ItemLogo size={26} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.25 }}>
                        {t(item.labelKey)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('connectAi.stepsCount', { count: item.steps.length })}
                      </Typography>
                    </Box>
                    <ArrowForwardIcon
                      className="connect-ai-arrow"
                      aria-hidden="true"
                      sx={{ fontSize: 18, color: 'text.secondary', transition: 'transform 160ms ease, color 160ms ease' }}
                    />
                  </ButtonBase>
                );
              })}
            </Box>
          </DialogContent>
        </>
      )}
    </Dialog>
  );
}
